import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, Check, TrendingUp, Calendar, Hash, CheckCircle2, XCircle, HelpCircle, Shield } from 'lucide-react';
import { TOP_100_WALLETS } from '../data/wallets';
import { formatBTC, formatUSD, formatPercent, formatNumber, formatDate, copyToClipboard, getBlockchainExplorerUrl } from '../utils/formatters';
export function WalletDetailPage() {
  const {
    address
  } = useParams<{
    address: string;
  }>();
  const wallet = TOP_100_WALLETS.find(w => w.address === address);
  if (!wallet) {
    return <div className="container mx-auto px-4 py-12" data-id="element-297">
        <div className="text-center" data-id="element-298">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-299">
            Wallet Not Found
          </h1>
          <Link to="/top100" className="text-blue-400 hover:text-blue-300" data-id="element-300">
            View Top 100 Wallets
          </Link>
        </div>
      </div>;
  }
  const getVerificationStatus = () => {
    if (wallet.verified === true) {
      return {
        icon: <CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-301" />,
        text: 'Verified Owner',
        description: 'Ownership confirmed through multiple reliable sources',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20'
      };
    }
    if (wallet.verified === false) {
      return {
        icon: <XCircle className="w-6 h-6 text-red-400" data-id="element-302" />,
        text: 'Disputed Ownership',
        description: 'Ownership claims are contested or unverified',
        color: 'text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/20'
      };
    }
    return {
      icon: <HelpCircle className="w-6 h-6 text-gray-500" data-id="element-303" />,
      text: 'Uncertain Ownership',
      description: 'Owner identity not confirmed or unknown',
      color: 'text-gray-500',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/20'
    };
  };
  const verificationStatus = getVerificationStatus();
  return <div className="min-h-screen bg-gray-950" data-id="element-304">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-305">
        <Link to="/top100" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8" data-id="element-306">
          <ArrowLeft className="w-4 h-4" data-id="element-307" />
          Back to Top 100
        </Link>

        {/* Header */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 mb-8" data-id="element-308">
          <div className="flex items-start justify-between mb-8" data-id="element-309">
            <div data-id="element-310">
              <div className="text-sm text-gray-400 mb-2" data-id="element-311">
                Rank #{wallet.rank}
              </div>
              <h1 className="text-3xl font-bold text-white mb-4" data-id="element-312">
                Wallet Details
              </h1>
            </div>
          </div>

          <div className="space-y-6" data-id="element-313">
            {/* Owner Verification Section - NEW */}
            <div className={`${verificationStatus.bgColor} border ${verificationStatus.borderColor} rounded-xl p-6 mb-8`} data-id="element-314">
              <div className="flex items-start gap-4" data-id="element-315">
                <div className="flex-shrink-0" data-id="element-316">{verificationStatus.icon}</div>
                <div className="flex-1" data-id="element-317">
                  <div className="flex items-center gap-3 mb-2" data-id="element-318">
                    <h3 className={`text-lg font-bold ${verificationStatus.color}`} data-id="element-319">
                      {verificationStatus.text}
                    </h3>
                    <Shield className={`w-5 h-5 ${verificationStatus.color}`} data-id="element-320" />
                  </div>
                  <p className="text-gray-400 text-sm mb-3" data-id="element-321">
                    {verificationStatus.description}
                  </p>
                  {wallet.label && <div className="mb-2" data-id="element-322">
                      <span className="text-gray-400 text-sm" data-id="element-323">
                        Identified as:{' '}
                      </span>
                      <span className="text-white font-medium" data-id="element-324">
                        {wallet.label}
                      </span>
                      {wallet.entity && <span className="text-gray-400 text-sm" data-id="element-325">
                          {' '}
                          ({wallet.entity})
                        </span>}
                    </div>}
                  {wallet.sources && wallet.sources.length > 0 && <div className="mt-3" data-id="element-326">
                      <span className="text-gray-400 text-sm block mb-2" data-id="element-327">
                        Verification Sources:
                      </span>
                      <div className="flex flex-wrap gap-2" data-id="element-328">
                        {wallet.sources.map((source, index) => <a key={index} href={source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 transition-colors" data-id="element-329">
                            <ExternalLink className="w-3 h-3" data-id="element-330" />
                            Source {index + 1}
                          </a>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            {/* Address */}
            <div data-id="element-331">
              <div className="text-sm text-gray-400 mb-2" data-id="element-332">Address</div>
              <div className="text-white font-mono text-lg break-all bg-gray-950 p-4 rounded border border-gray-800" data-id="element-333">
                {wallet.address}
              </div>
            </div>

            {/* Balance */}
            <div data-id="element-334">
              <div className="text-sm text-gray-400 mb-2" data-id="element-335">Balance</div>
              <div className="text-3xl font-bold text-white" data-id="element-336">
                {formatNumber(wallet.balance)} ETH
              </div>
            </div>

            {/* Percentage */}
            <div data-id="element-337">
              <div className="text-sm text-gray-400 mb-2" data-id="element-338">
                Percentage of Total Supply
              </div>
              <div className="text-2xl font-bold text-blue-400" data-id="element-339">
                {formatPercent(wallet.percentage)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}